import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, type ReactNode, useCallback, useRef, useState } from 'react'
import { Dropdown } from '#components/Dropdown'
import { Icon } from '#components/Icon'
import CheckmarkIcon from '#icons/check-24.svg'
import BurgerMenuIcon from '#icons/justified-24.svg'
import { fragments, style } from '#styles/index'
import { sharedTestIds } from '#utils/sharedTestIds'
import { useIntersections } from '#utils/useIntersections'

const headerTabsClass = css`
  display: flex;
  flex: 1;

  flex-basis: auto;
  overflow: hidden;
  justify-self: stretch;

  > ._tabs-container {
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1;
    flex-basis: auto;
    overflow: hidden;

    > ._tabs-rail {
      flex: 1;
      flex-basis: auto;
      position: absolute;

      left: 0;
      width: 100%;
      height: 100%;
      transition: 0.25s ease opacity;
      display: flex;
      flex-direction: row;
      overflow: hidden;

      &._visible {
        opacity: 1;
      }

      &._invisible {
        opacity: 0;
        pointer-events: none;
      }

      > ._tabs {
        flex: 1;
        width: fit-content;
        height: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        gap: 24px;

        &._overlay {
          gap: 12px;
        }
      }
    }
  }
`

const tabClass = css`
  position: relative;
  max-width: 230px;
  color: ${style.theme.textDefault};
  ${fragments.textStyle.body.m.condensedBold};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  &:after {
    content: ' ';
    position: absolute;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    background-color: ${style.theme.borderTransparent};
    transition: ease 0.25s all;
  }
  
  &:hover:after {
    background-color: ${style.theme.backgroundSurfaceAccentStrong};
    transition: ease 0.25s all;
  }
  
  &._highlighted:after  {
    background-color: ${style.theme.borderInput};
    transition: ease 0.25s all;
  }

  &[aria-selected="true"] {
    color:${style.theme.textAccent};
    transition: ease 0.25s all; 
    &:after {
      background-color: ${style.theme.textAccent};
    }
  }
  
  > ._chevron {
    cursor: pointer;
    color: ${style.theme.textAccent};

    
    margin-right: 4px;
    transition: ease 0.25s transform;  

    &._open {
      transform: rotateX(180deg);
      color: ${style.theme.textAccent};
    }
  }

  > ._entry, > ._dropdown-anchor > ._entry {
    cursor: pointer;
    user-select: none;
    max-width: 230px;
    height: 100%;
    padding: 0 4px 0 8px;
    color: currentColor;    
    ${fragments.textStyle.body.m.condensedBold};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;    
    transition: ease 0.25s color;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    &._with-chevron {
      padding-right: 0;
    }
  }

  &[aria-selected="true"] {
    > ._entry {
        color:${style.theme.textAccent};
    }
  }
  
  &._pending > ._entry {
    cursor: progress;
    color: ${style.theme.textSoft};
  }
`

const dropdownClass = css`
  padding: 8px 0px;
  display: flex;
  flex-direction: column;

  > ._dropdown-entry {
    cursor: pointer;
    transition: ease 0.15s background-color;

    &._selected {
      background-color: ${style.theme.backgroundSurfaceTertiary};
      > ._entry > ._icon {
        color: ${style.theme.textAccent}
      }
    }
    
    &:hover {
      background-color: ${style.theme.backgroundSurfaceTertiaryStrong};
    }

    > ._entry { 
      user-select: none;
      padding: 6px 12px;
      ${fragments.textStyle.body.s.regular};
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;

      > ._icon {
        color: transparent;
      }
    }

    &._pending {
      cursor: progress;
      background-color: ${style.theme.backgroundSurfacePrimary};
     
      > ._entry {
        color: ${style.theme.textSoft};
      }
    }
  }
`

export const HeaderDropdownEntry: FC<HTMLProps<HTMLDivElement> & { selected?: boolean }> = ({ children, className, selected, ...rest }) => {
  return (
    <div className={cx(className, '_entry', selected && '_selected')} {...rest}>
      {children}
      <Icon className="_icon" iconUrl={CheckmarkIcon} />
    </div>
  )
}

export const HeaderTabEntry: FC<HTMLProps<HTMLDivElement>> = ({ children, className, selected, ...rest }) => (
  <div {...rest} className={cx('_entry', className)}>
    {children}
  </div>
)

export type HeaderTabEntryDefinition = {
  key: string
  pending?: boolean
  /** Disables the selection of this item */
  cannotBeSelected?: boolean
  /** Puts a dark bar beneath the header element, if not directly selected */
  highlighted?: boolean
  tab: ReactNode
  /**
   * The component is wrapped in a div, which can be controlled.
   * There is a class `_selected` attached, if the tab is the current selection.
   * This allows you to write `&._selected { ... }` css
   */
  tabWrappingClass?: string
  dropdown: ReactNode
  /**
   * The dropdown is wrapped in a div, which can be controlled
   * There is a class `_selected` attached, if the entry is the current selection.
   * This allows you to write `&._selected { ... }` css
   */
  dropdownWrappingClass?: string
  alwaysVisible?: boolean
}

const burgerMenuClass = css`
  cursor: pointer;
  padding: 4px 4px 12px 4px;
  display: flex;
  align-items: center;
  align-self: flex-end;
  transition: all 250ms;

  &.disabled, &.disabled:hover {
    cursor: unset;
    color: ${style.theme.textDisabled};
  }

  &:hover {
    color: ${style.theme.textAccent};
    transition: all 250ms;
  }

`

/**
 * Header Tabs for displaying a solution for collapsing tabs.
 * Best paired with {@link HeaderDropdownEntry} and {@link HeaderTabEntry}.
 */
export const HeaderTabs: FC<
  {
    className?: string
    entries: HeaderTabEntryDefinition[]
  } & ({ selection?: string; setSelection?: (s: string) => unknown } | undefined)
> = ({ className, entries, selection: passedSelection, setSelection: setSelectionCallback }) => {
  const { addRef, getEntries } = useIntersections(undefined, { threshold: [1] })
  const allVisible = getEntries().every((x) => !x.lastEntry || x.lastEntry.intersectionRatio >= 1)
  const [open, setOpen] = useState(false)

  const [storedSelection, setStoredSelection] = useState<string>()
  const selection = passedSelection ?? storedSelection
  const currentSelection = entries.find((x) => x.key === selection)
  const firstEntry: (typeof entries)[number] | undefined = currentSelection ?? entries[0]
  const alwaysVisible = entries.filter((x) => x.alwaysVisible && x !== firstEntry)
  const chevronRef = useRef<HTMLDivElement>(null)
  const setSelection = useCallback(
    (selection: string) => {
      const entry = entries.find((x) => x.key === selection)
      if (!entry || entry.pending || entry.cannotBeSelected) {
        return
      }
      setStoredSelection(selection)
      setSelectionCallback?.(selection)
    },
    [entries, setSelectionCallback],
  )

  const anyDropdowns = entries.some((x) => !!x.dropdown) || true

  return (
    <div className={cx(headerTabsClass, className)}>
      <div className="_tabs-container">
        <div className={cx('_tabs-rail', allVisible ? '_visible' : '_invisible')}>
          <div className="_tabs">
            {entries.map((child, index) => {
              return (
                <div
                  key={child.key}
                  className={cx(tabClass, '_tab', child.pending && '_pending', child.highlighted && '_highlighted', child.tabWrappingClass)}
                  role="tab"
                  aria-selected={child?.key === selection}
                  ref={(ref) => {
                    addRef(ref, index)
                  }}
                  onClick={() => setSelection(child.key)}
                  data-testid={sharedTestIds.nav.tab(index)}
                >
                  {child.tab}
                </div>
              )
            })}
          </div>
        </div>

        <div className={cx('_tabs-rail', allVisible ? '_invisible' : '_visible')}>
          <div className="_tabs _overlay">
            {anyDropdowns && (
              <Dropdown
                withoutArrow
                offset={{
                  mainAxis: 8,
                  crossAxis: -12,
                }}
                dropdownContent={entries.map((child) => (
                  <div
                    key={child.key}
                    className={cx('_dropdown-entry', child.pending && '_pending', child.highlighted && '_highlighted', child.dropdownWrappingClass)}
                    role="tab"
                    aria-selected={child?.key === selection}
                    onClick={() => setSelection(child.key)}
                  >
                    {child.dropdown}
                  </div>
                ))}
                popupClassName={dropdownClass}
                open={open}
                onOpenChange={setOpen}
                placement="bottom-start"
                overridingPosition={chevronRef.current}
              />
            )}

            <div
              className={cx(
                tabClass,
                '_tab',
                firstEntry?.pending && '_pending',
                firstEntry?.highlighted && '_highlighted',
                firstEntry?.tabWrappingClass,
              )}
              role="tab"
              aria-selected={firstEntry?.key === selection}
            >
              {firstEntry?.tab}
            </div>

            {anyDropdowns && (
              <div className={cx(tabClass, '_tab')}>
                <div className={burgerMenuClass}>
                  <Icon
                    ref={chevronRef}
                    iconUrl={BurgerMenuIcon}
                    size="m"
                    className={cx('_chevron', open && '_open')}
                    onClick={() => setOpen(!open)}
                  />
                </div>
              </div>
            )}

            {alwaysVisible.map((child) => {
              return (
                <div
                  key={child.key}
                  className={cx(tabClass, '_tab', child.pending && '_pending', child.highlighted && '_highlighted', child.tabWrappingClass)}
                  role="tab"
                  aria-selected={child.key === selection}
                  onClick={() => setSelection(child.key)}
                >
                  {child.tab}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
